/**
 * @author Rahul Nainala
 * @description This Component is used for editing and creating Custom Hierarchy
 */
import './styles.css';
import TextArea from 'antd/lib/input/TextArea';
import React, { useEffect, useState } from 'react';
import { Button, Card, Select, Tree, Form, Popover, message, Checkbox, Input } from 'antd';
import {
  AppstoreAddOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined
} from '@ant-design/icons';
import {
  createHierarchy,
  getModuleListbyId,
  getHierarchy,
  getDropdownData,
  getTreeDataCall,
  editHierarchy,
  deleteHierarchy,
  EditDatabyId,
  getParamsData,
  postParamsData
} from './apicalls';

const { Option } = Select;

const EditAssetsTree = (props) => {
  const [form] = Form.useForm();
  const [paramsForm] = Form.useForm();
  const [minLimit, setMinLimit] = useState('');
  const [treeData, setTreeData] = useState([]);
  const [paramsId, setParamsId] = useState(null);
  const [maxLimit, setMaxLimit] = useState(null);
  const [levelCode, setLevelCode] = useState('');
  const [parentId, setParentId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(false);
  const [moduleData, setModuleData] = useState({});
  const [moduleName, setModuleName] = useState('');
  const [hasDevice, setHasDevice] = useState(false);
  const [deviceData, setDeviceData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectionData, setSelectionData] = useState([]);
  const [optionChange, setOptionChange] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showParamsForm, setShowParamsFrom] = useState(false);
  const [selectedNodeName, setSelectedNodeName] = useState('');
  const [levelType, setLevelType] = useState('');
  const [visiblePopoverNodeId, setVisiblePopoverNodeId] = useState(null);

  const handleExpand = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue);
  };

  useEffect(() => {
    try {
      const idFromUrl = props.location.pathname.split('/');
      const fetchModuleData = async () => {
        const ModuleData = await getModuleListbyId(idFromUrl[3]);
        setModuleData(ModuleData[0]);
        setModuleName(ModuleData[0].AliasCode);
      };
      fetchModuleData();
    } catch (e) {
      message.error('Unable to Load Modules');
      console.error('Error :', e);
    }
  }, [props.location.pathname]);

  useEffect(() => {
    try {
      const loadHierarchy = async () => {
        if (Object.keys(moduleData).length === 0) return;
        const propstoHierarchy = {
          LevelType: 'Module',
          Code: moduleData.AliasCode,
          Type: 'module',
          parent: null,
          nextlevels: []
        };
        try {
          const hierarchyData = await getHierarchy(propstoHierarchy);
          if (hierarchyData && hierarchyData.length > 0) {
            setTreeData(formatTreeData(hierarchyData));
            setExpandedKeys(getAllKeys(hierarchyData));
          } else {
            try {
              await createHierarchy(propstoHierarchy);
              const newHierarchy = await getHierarchy(propstoHierarchy);
              setExpandedKeys(getAllKeys(newHierarchy));
              setTreeData(formatTreeData(newHierarchy));
            } catch (e) {
              message.error('Error Inserting data ', e);
            }
          }
        } catch (e) {
          message.error('Error handling hierarchy data ', e);
        }
      };
      loadHierarchy();
    } catch (e) {
      message.error('Loading  API Error ', e);
    }
  }, [moduleData]);

  const getAllKeys = (nodes) => {
    let keys = [];
    nodes.forEach((node) => {
      keys.push(node.Id);
      if (node.nextLevels) {
        keys = keys.concat(getAllKeys(node.nextLevels));
      }
    });
    return keys;
  };

  const formatTreeData = (data) => {
    if (!Array.isArray(data)) {
      message.error('Expected data to be an array but got:', data);
      return [];
    }
    return data.map(
      (item) => (
        setParentId(item.key),
        setMaxLimit(item.MaxLimit),
        setMinLimit(item.MinLimit),
        {
          Id: item.Id,
          title: item.Name,
          key: item.key,
          levelType: item.LevelType,
          type: item.Type,
          parentId: item.ParentId,
          maxLimit: item.MaxLimit,
          minLimit: item.MinLimit,
          nextLevels: item.nextLevels ? formatTreeData(item.nextLevels) : []
        }
      )
    );
  };

  const handleCreateClick = (node) => {
    form.resetFields();
    setFormData(false);
    setShowForm(true);
    setShowParamsFrom(false);
    setIsEditMode(false);
    setSelectedNode(node);
    setSelectedNodeName(node.title);
    setLevelType(node.levelType);
  };

  const handleEditClick = async (node) => {
    setShowParamsFrom(false);
    setSelectedNodeName(node.title);
    setLevelType(node.levelType);
    let response = await EditDatabyId(node.key, moduleName);
    if (response && Array.isArray(response)) {
      const existingOptionsCodes = response
        .filter((item) => item.LevelType === 'Asset')
        .map((item) => item.Name);
      await fetchNextLevel(node.type);
      const foundDeviceData = response.find((item) => item.LevelType === 'Device');
      if (foundDeviceData) {
        setDeviceData(foundDeviceData);
        setHasDevice(true);
        setOptionChange('Device');
        setLevelCode(foundDeviceData.LevelCode);
        form.setFieldsValue({
          selectType: foundDeviceData.LevelType,
          levelCode: foundDeviceData.LevelCode
        });
      } else {
        setHasDevice(false);
        setDeviceData(null);
      }
      const assetData = response.find((item) => item.LevelType === 'Asset');
      if (assetData) {
        setLevelCode(assetData.LevelCode);
        setFormData(true);
        setOptionChange(assetData.LevelType);
        form.setFieldsValue({
          selectType: assetData.LevelType,
          levelCode: assetData.LevelCode,
          availableOptions: existingOptionsCodes
        });
      } else {
        setFormData(false);
      }

      setSelectionData((data) => {
        const matchedOptions = data.filter((option) => existingOptionsCodes.includes(option.Name));
        setSelectedOptions(matchedOptions.map((option) => option.Name));
        return data;
      });
    }
    setIsEditMode(true);
    setSelectedNode(node);
    setShowForm(true);
  };

  const handleDeleteClick = (node) => {
    setSelectedNode(node);
    setVisiblePopoverNodeId(node.Id);
  };

  const handleAdditionalParamsClick = async (node) => {
    try {
      await new Promise((resolve) => setTimeout(resolve(setParamsId(node.Id)), 0));
      setShowForm(false);
      setShowParamsFrom(true);
      setSelectedNodeName(node.title);
      setLevelType(node.levelType);
      let response = await getParamsData(node.Id);
      paramsForm.setFieldsValue({
        minLimit: response.MinLimit || '',
        maxLimit: response.MaxLimit || ''
      });
    } catch (error) {
      console.error('Error fetching params:', error);
      message.error('Failed to retrieve parameters');
    }
  };

  const handleOptionChange = async (value) => {
    setSelectedOptions([]);
    if (value === 'Asset') {
      setOptionChange(value);
      setHasDevice(true);
      setFormData(true);
      await fetchNextLevel(selectedNode.type);
    } else {
      setOptionChange('Device');
      setHasDevice(false);
      selectedNode.levelType = 'Device';
      setFormData(false);
    }
  };

  const fetchNextLevel = async (parentId) => {
    const data = await getDropdownData(parentId);
    setSelectionData(data);
  };

  const handleAdditionalParams = async () => {
    await paramsForm.validateFields();
    let postData = {
      MinLimit: minLimit,
      MaxLimit: maxLimit
    };
    const response = await postParamsData(postData, paramsId);
    if (response.status === 200) {
      message.success(response.message);
    }
    paramsForm.resetFields();
    setShowParamsFrom(false);
  };

  const handleCreate = async () => {
    await form.validateFields();
    if (!selectedNode) {
      message.error('No node selected for saving.');
      return;
    }
    const currentParentId = selectedNode.key || parentId;
    const updatedChildren = selectedOptions
      .map((optionId) => {
        const selectedOption = selectionData.find((option) => option.Name === optionId);
        if (!selectedOption) {
          console.error(`No matching option found for ID: ${optionId}`);
          return null;
        }
        return {
          Type: selectedOption.Type,
          Code: selectedOption.AliasCode
        };
      })
      .filter(Boolean);
    const postData = [];
    postData.push({
      ModuleCode: moduleName,
      parentId: currentParentId,
      LevelType: optionChange,
      LevelCode: levelCode,
      nextLevels: updatedChildren
    });
    if (hasDevice && optionChange !== 'Device' && levelType !== 'Module') {
      postData.push({
        ModuleCode: moduleName,
        parentId: currentParentId,
        LevelType: 'Device',
        nextLevels: []
      });
    }
    let response = await createHierarchy(postData);
    if (response.status === 200) {
      message.success(response.message);
    }
    setSelectedOptions([]);
    setLevelCode('');
    form.resetFields();
    setShowForm(false);
    let updatedData = await getTreeDataCall(moduleName);
    setTreeData(formatTreeData(updatedData));
    setExpandedKeys(getAllKeys(updatedData));
  };

  const handleEdit = async () => {
    try {
      await form.validateFields();
      if (!selectedNode) throw new Error('No node selected for saving.');
      const currentParentId = selectedNode.key || parentId;
      const updatedChildren = selectedOptions.map((optionId) => ({
        Type: selectionData.find((option) => option.Name === optionId).Type,
        Code: selectionData.find((option) => option.Name === optionId).AliasCode
      }));
      const postData = [];
      postData.push({
        ModuleCode: moduleName,
        parentId: currentParentId,
        LevelType: optionChange,
        LevelCode: levelCode,
        nextLevels: updatedChildren
      });
      if (hasDevice && optionChange !== 'Device' && levelType !== 'Module') {
        postData.push({
          ModuleCode: moduleName,
          parentId: currentParentId,
          LevelType: 'Device',
          nextLevels: []
        });
      }
      await editHierarchy(postData);
      setSelectedOptions([]);
      form.resetFields();
      setShowForm(false);
      setLevelCode('');
      let updatedData = await getTreeDataCall(moduleName);
      setTreeData(formatTreeData(updatedData));
      setExpandedKeys(getAllKeys(updatedData));
    } catch (error) {
      console.error('erorr', error);
    }
  };

  const confirmDelete = async () => {
    if (!selectedNode) return;
    let response = await deleteHierarchy(selectedNode.Id);
    setVisiblePopoverNodeId(null);
    if (response.status === 200) {
      message.success(response.message);
    }
    setShowForm(false);
    let updatedData = await getTreeDataCall(moduleName);
    setTreeData(formatTreeData(updatedData));
    setExpandedKeys(getAllKeys(updatedData));
  };

  const cancelDelete = () => {
    setShowForm(false);
    setVisiblePopoverNodeId(null);
  };

  const renderTreeNodes = (nodes) => {
    return nodes.map((node) => ({
      title: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {node.type === 'Device' ? (
              <>
                <CheckCircleOutlined style={{ color: 'green', marginRight: 8 }} />
                {node.title}
                {/* <Button
                  type="default"
                  style={{
                    marginLeft: '10px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  onClick={() => handleAdditionalParamsClick(node)}
                >
                  <AppstoreAddOutlined style={{ marginRight: 4 }} />
                </Button> */}
                <Popover
                  content={
                    <div>
                      <p>Are you sure you want to delete this item?</p>
                      <Button onClick={confirmDelete} type="primary">
                        OK
                      </Button>
                      <Button onClick={cancelDelete} style={{ marginLeft: 8 }}>
                        Cancel
                      </Button>
                    </div>
                  }
                  title="Confirm Delete"
                  trigger="click"
                  open={visiblePopoverNodeId === node.Id}
                  onOpenChange={(visible) => !visible && cancelDelete()}
                >
                  <Button
                    type="default"
                    style={{
                      marginLeft: '10px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onClick={() => handleDeleteClick(node)}
                  >
                    <DeleteOutlined style={{ marginRight: 4 }} />
                  </Button>
                </Popover>
              </>
            ) : (
              // <CloseCircleOutlined style={{ marginRight: 8 }} />
              <>{node.title}</>
            )}
          </span>
          {node.type !== 'Device' && (
            <>
              {node.nextLevels && node.nextLevels.length > 0 ? (
                <Button
                  type="default"
                  style={{
                    marginLeft: '10px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  onClick={async () => {
                    setShowForm(false);
                    await new Promise((resolve) => setTimeout(resolve, 0));
                    handleEditClick(node);
                  }}
                >
                  <EditOutlined style={{ marginRight: 4 }} />
                </Button>
              ) : (
                <Button
                  type="default"
                  style={{
                    marginLeft: '10px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  onClick={async () => {
                    setShowForm(false);
                    await new Promise((resolve) => setTimeout(resolve, 0));
                    handleCreateClick(node);
                  }}
                >
                  <PlusOutlined style={{ marginRight: 4 }} />
                </Button>
              )}
              {node.type !== 'module' && (
                <Button
                  type="default"
                  style={{
                    marginLeft: '10px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  onClick={() => handleAdditionalParamsClick(node)}
                >
                  <AppstoreAddOutlined style={{ marginRight: 4 }} />
                </Button>
              )}

              <Popover
                content={
                  <div>
                    <p>Are you sure you want to delete this item?</p>
                    <Button onClick={confirmDelete} type="primary">
                      OK
                    </Button>
                    <Button onClick={cancelDelete} style={{ marginLeft: 8 }}>
                      Cancel
                    </Button>
                  </div>
                }
                title="Confirm Delete"
                trigger="click"
                open={visiblePopoverNodeId === node.Id}
                onOpenChange={(visible) => !visible && cancelDelete()}
              >
                <Button
                  type="default"
                  style={{
                    marginLeft: '10px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  onClick={() => handleDeleteClick(node)}
                >
                  <DeleteOutlined style={{ marginRight: 4 }} />
                </Button>
              </Popover>
            </>
          )}
        </div>
      ),
      key: node.Id,
      children: node.nextLevels ? renderTreeNodes(node.nextLevels) : []
    }));
  };

  return (
    <>
      <div>
        <Button
          style={{ marginLeft: '20px' }}
          onClick={() => props.navigate('/rubus/customHierarchy')}
        >
          Go Back
        </Button>
      </div>
      <div style={{ display: 'flex', padding: '20px' }}>
        <div style={{ width: '50%', marginRight: '20px' }}>
          <Card
            className="card-tree"
            title="Hierarchy Tree"
            style={{ minHeight: window.innerHeight - 150, color: '#fffff', fontSize: '30px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', height: '100%' }}>
              <div
                style={{
                  flexGrow: 1,
                  overflowY: 'auto',
                  maxHeight: '800px',
                  background: '#1a3652'
                }}
              >
                <Tree
                  treeData={renderTreeNodes(treeData)}
                  selectable={false}
                  showLine
                  expandedKeys={expandedKeys}
                  onExpand={handleExpand}
                />
              </div>
            </div>
          </Card>
        </div>
        <div style={{ flex: 1, marginRight: '50px' }}>
          {showForm &&
            (isEditMode ? (
              <Card
                className="card-form"
                title={`Hierarchy Selections : ${selectedNodeName}`}
                style={{ minHeight: window.innerHeight - 150 }}
              >
                <Form form={form} layout="vertical" onFinish={handleEdit}>
                  <Form.Item
                    label="Select Type"
                    name="selectType"
                    rules={[{ required: true, message: 'Please Select a Type!' }]}
                  >
                    <Select placeholder="Select Asset or Device" onChange={handleOptionChange}>
                      <Option value="Asset">Asset</Option>
                      <Option value="Device">Device</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    label="Level Code"
                    name="levelCode"
                    rules={[{ required: true, message: 'Please input Level Code!' }]}
                  >
                    <Input.TextArea
                      id="levelCode"
                      disabled={true}
                      placeholder="Enter Level Code"
                      onChange={(e) => setLevelCode(e.target.value)}
                      style={{ height: '30px' }}
                    />
                  </Form.Item>
                  {selectionData && selectionData.length > 0 && formData && (
                    <Form.Item
                      label="Available Options"
                      name="availableOptions"
                      rules={[{ required: true, message: 'Please Select an Asset Type!' }]}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Select Asset Type"
                        onChange={(values) => setSelectedOptions(values)}
                        value={selectedOptions}
                      >
                        {selectionData.map((option) => (
                          <Option key={option.Id} value={option.Name}>
                            {option.Name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {formData && levelType !== 'Module' && (
                    <div style={{ marginTop: '20px' }}>
                      <Form.Item>
                        <Checkbox
                          checked={hasDevice}
                          onChange={(e) => {
                            setHasDevice(e.target.checked);
                            if (e.target.checked && deviceData) {
                              form.setFieldsValue({
                                deviceMinLimit: deviceData.MinLimit,
                                deviceMaxLimit: deviceData.MaxLimit
                              });
                            }
                          }}
                        >
                          <span style={{ color: 'white' }}>Has Device</span>
                        </Checkbox>
                      </Form.Item>
                      {hasDevice && (
                        <div>
                          <Form.Item
                            label="Select Device"
                            name="deviceType"
                            className="white-label"
                          >
                            <Select defaultValue="Device" disabled></Select>
                          </Form.Item>
                        </div>
                      )}
                    </div>
                  )}
                  <Button type="primary" htmlType="submit">
                    Update
                  </Button>
                  <Button
                    type="default"
                    onClick={() => {
                      const currentLevelCode = form.getFieldValue('levelCode');
                      form.resetFields();
                      form.setFieldsValue({
                        levelCode: currentLevelCode
                      });
                    }}
                    style={{ marginLeft: '10px' }}
                  >
                    Clear
                  </Button>
                </Form>
              </Card>
            ) : (
              <Card
                className="card-form"
                title={`Hierarchy Selections : ${selectedNodeName}`}
                style={{ minHeight: window.innerHeight - 150 }}
              >
                <Form form={form} layout="vertical" onFinish={handleCreate}>
                  <Form.Item
                    label="Select Type"
                    name="selectType"
                    rules={[{ required: true, message: 'Please Select a Type!' }]}
                  >
                    <Select placeholder="Select Asset or Device" onChange={handleOptionChange}>
                      <Option value="Asset">Asset</Option>
                      <Option value="Device">Device</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    label="Level Code"
                    name="levelCode"
                    rules={[{ required: true, message: 'Please input Level Code!' }]}
                  >
                    <Input.TextArea
                      id="levelCode"
                      placeholder="Enter Level Code"
                      onChange={(e) => setLevelCode(e.target.value)}
                      style={{ height: '30px' }}
                    />
                  </Form.Item>
                  {selectionData.length > 0 && formData && (
                    <>
                      <Form.Item
                        label="Available Options"
                        name="availableOptions"
                        rules={[{ required: true, message: 'Please Select an Asset Type!' }]}
                      >
                        <Select
                          mode="multiple"
                          placeholder="Select Asset Type"
                          onChange={(values) => setSelectedOptions(values)}
                        >
                          {selectionData.map((option) => (
                            <Option key={option.Id} value={option.Name} type={option.Type}>
                              {option.Name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </>
                  )}
                  {formData && levelType !== 'Module' && (
                    <div style={{ marginTop: '20px' }}>
                      <Form.Item>
                        <Checkbox
                          checked={hasDevice}
                          onChange={(e) => setHasDevice(e.target.checked)}
                        >
                          <span style={{ color: 'white' }}>Has Device</span>
                        </Checkbox>
                      </Form.Item>
                      {hasDevice && (
                        <div>
                          <Form.Item label="Select Device" name="deviceType">
                            <Select defaultValue="Device" disabled></Select>
                          </Form.Item>
                        </div>
                      )}
                    </div>
                  )}
                  <Button type="primary" htmlType="submit">
                    Create
                  </Button>
                  <Button
                    type="default"
                    onClick={() => {
                      setMinLimit('');
                      setMaxLimit('');
                      form.resetFields();
                    }}
                    style={{ marginLeft: '10px' }}
                  >
                    Clear
                  </Button>
                </Form>
              </Card>
            ))}
          {showParamsForm && (
            <Card
              className="card-form"
              title={`Additional Parameters : ${selectedNodeName}`}
              style={{ minHeight: window.innerHeight - 150 }}
            >
              <Form form={paramsForm} layout="vertical" onFinish={handleAdditionalParams}>
                <Form.Item
                  label="Min Limit"
                  name="minLimit"
                  rules={[
                    {
                      required: true,
                      message: 'Please input minimum limit!'
                    },
                    {
                      pattern: /^[0-9]+$/,
                      message: 'Only numeric values are allowed!'
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const maxLimit = getFieldValue('maxLimit');
                        if (maxLimit && value && parseInt(value) > parseInt(maxLimit)) {
                          return Promise.reject(
                            new Error('Minimum limit cannot be greater than maximum limit')
                          );
                        }
                        return Promise.resolve();
                      }
                    })
                  ]}
                >
                  <TextArea
                    placeholder="Enter Minimum Limit"
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '');
                      setMinLimit(numericValue);
                    }}
                    defaultValue={minLimit}
                    style={{ height: '30px' }}
                  />
                </Form.Item>

                <Form.Item
                  label="Max Limit"
                  className="white-label"
                  name="maxLimit"
                  rules={[
                    {
                      pattern: /^[0-9]+$/,
                      message: 'Only numeric values are allowed!'
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const minLimit = getFieldValue('minLimit');
                        if (minLimit && value && parseInt(value) < parseInt(minLimit)) {
                          return Promise.reject(
                            new Error('Maximum limit cannot be less than minimum limit')
                          );
                        }
                        return Promise.resolve();
                      }
                    })
                  ]}
                >
                  <TextArea
                    placeholder="Enter Maximum Limit"
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '');
                      setMaxLimit(numericValue);
                    }}
                    defaultValue={maxLimit}
                    style={{ height: '30px' }}
                  />
                </Form.Item>

                <Button type="primary" htmlType="submit">
                  Update
                </Button>
                <Button
                  type="default"
                  onClick={() => {
                    setMinLimit('');
                    setMaxLimit('');
                    paramsForm.resetFields();
                  }}
                  style={{ marginLeft: '10px' }}
                >
                  Clear
                </Button>
              </Form>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default EditAssetsTree;